/**
 * <s-bg-video> — muted, looping background video (homepage hero slides).
 *
 * Wraps a single <video muted loop playsinline>. Plays while the element is on
 * screen and, when it is a slideshow slide ([data-slide]), only while that slide
 * carries [data-active] (set by <s-hero-slideshow>); an inactive slide rewinds so
 * it starts from the top next time. <s-hero-slideshow> turns off looping so each clip plays once. Nudges play() for browsers (iOS Low Power
 * Mode etc.) that ignore autoplay, and stays paused on the poster frame when the
 * visitor prefers reduced motion.
 */
class SBgVideo extends HTMLElement {
  #ac;
  #intersection;
  #attributes;
  #visible = true;

  connectedCallback() {
    const video = this.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    video.muted = true;
    video.playsInline = true;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const isSlide = this.hasAttribute('data-slide');
    this.#ac = new AbortController();

    const sync = () => {
      const active = !isSlide || this.hasAttribute('data-active');
      if (!active && isSlide) {
        video.pause();
        video.currentTime = 0;
        return;
      }
      if (reduceMotion.matches || !this.#visible) {
        video.pause();
      } else {
        video.play().catch(() => {});
      }
    };

    reduceMotion.addEventListener('change', sync, { signal: this.#ac.signal });

    this.#intersection = new IntersectionObserver((entries) => {
      this.#visible = entries.some((entry) => entry.isIntersecting);
      sync();
    });
    this.#intersection.observe(this);

    if (isSlide) {
      this.#attributes = new MutationObserver(sync);
      this.#attributes.observe(this, { attributes: true, attributeFilter: ['data-active'] });
    }

    sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#intersection?.disconnect();
    this.#attributes?.disconnect();
  }
}

if (!customElements.get('s-bg-video')) {
  customElements.define('s-bg-video', SBgVideo);
}
